<template>
  <!-- ============================================ -->
  <!-- FEATURES & AUDIENCE - Estilo 11x.ai sobre fondo cinematográfico -->
  <!-- ============================================ -->
  <section class="relative py-20 sm:py-28 overflow-hidden">
    <!-- Background cinematográfico (mismo estilo que HeroLanding) -->
    <div class="absolute inset-0">
      <div 
        class="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style="background-image: url('https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80')"
      ></div>
      <div class="absolute inset-0 bg-gray-950/95"></div>
      <div class="absolute inset-0 opacity-[0.03]" style="background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 256 256%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E')"></div>
    </div>

    <div class="relative z-10">

      <!-- ========================================== -->
      <!-- 1. "¿Para quién es Coolliving?" (PRIMERO) -->
      <!-- ========================================== -->
      <div class="mb-20 sm:mb-28">
        <div class="text-center mb-12 px-4 sm:px-6 lg:px-8">
          <h2 class="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight tracking-tight">
            {{ $t('whoWeServe.title') }}
          </h2>
          <p class="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
            {{ $t('whoWeServe.subtitle') }}
          </p>
        </div>

        <!-- Cards: scroll horizontal en mobile, grid en desktop -->
        <div class="audience-carousel">
          <div class="audience-carousel-track">
            <!-- Propietarios -->
            <div class="audience-carousel-card group">
              <div class="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300 h-full">
                <div class="w-12 h-12 bg-green-500/15 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <svg class="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <h4 class="text-lg font-semibold text-white mb-3">{{ $t('whoWeServe.owners.badge') }}</h4>
                <p class="text-gray-400 text-sm leading-relaxed mb-3">{{ $t('whoWeServe.owners.description') }}</p>
                <p class="text-gray-500 text-xs italic">{{ $t('whoWeServe.owners.ideal') }}</p>
              </div>
            </div>

            <!-- Agentes -->
            <div class="audience-carousel-card group">
              <div class="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300 h-full">
                <div class="w-12 h-12 bg-indigo-500/15 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <svg class="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h4 class="text-lg font-semibold text-white mb-3">{{ $t('whoWeServe.agents.badge') }}</h4>
                <p class="text-gray-400 text-sm leading-relaxed mb-3">{{ $t('whoWeServe.agents.description') }}</p>
                <p class="text-gray-500 text-xs italic">{{ $t('whoWeServe.agents.ideal') }}</p>
              </div>
            </div>

            <!-- Empresas -->
            <div class="audience-carousel-card group">
              <div class="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300 h-full">
                <div class="w-12 h-12 bg-purple-500/15 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h4 class="text-lg font-semibold text-white mb-3">{{ $t('whoWeServe.companies.badge') }}</h4>
                <p class="text-gray-400 text-sm leading-relaxed mb-3">{{ $t('whoWeServe.companies.description') }}</p>
                <p class="text-gray-500 text-xs italic">{{ $t('whoWeServe.companies.ideal') }}</p>
              </div>
            </div>

            <!-- Residentes — buscadores de coliving -->
            <div class="audience-carousel-card group">
              <div class="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300 h-full flex flex-col">
                <div class="w-12 h-12 bg-emerald-500/15 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <div class="flex items-center gap-2 mb-3">
                  <h4 class="text-lg font-semibold text-white">{{ $t('whoWeServe.residents.badge') }}</h4>
                  <span class="text-xs font-semibold bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">{{ $t('whoWeServe.residents.free') }}</span>
                </div>
                <p class="text-gray-400 text-sm leading-relaxed mb-3">
                  {{ $t('whoWeServe.residents.description') }}
                </p>
                <p class="text-gray-500 text-xs italic mb-4">{{ $t('whoWeServe.residents.ideal') }}</p>
                <router-link
                  to="/encuentratucoliving"
                  class="mt-auto inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 transition-colors text-sm font-medium"
                >
                  {{ $t('whoWeServe.residents.cta') }}
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- 2. Features Carousel (DESPUÉS) -->
      <!-- ========================================== -->
      <div>
        <!-- Header del carousel -->
        <div class="text-center mb-12 sm:mb-16 px-4 sm:px-6 lg:px-8">
          <div class="max-w-3xl mx-auto">
            <h3 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-5 leading-tight tracking-tight">
              {{ $t('featuresAndAudience.featuresTitle') }}
            </h3>
            <p class="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
              {{ $t('featuresAndAudience.featuresSubtitle') }}
            </p>
            <router-link
              :to="softwareLandingPath"
              class="inline-flex items-center px-6 py-3 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-colors duration-200 text-sm"
            >
              {{ $t('featuresAndAudience.softwareCta') }}
            </router-link>
          </div>
        </div>

        <!-- Carousel horizontal -->
        <div class="features-carousel">
          <div class="features-carousel-track">

            <!-- 1. Automatización de Estancias -->
            <div class="features-carousel-card group">
              <div class="features-card-image bg-gradient-to-br from-green-500/20 to-green-600/10">
                <svg class="w-12 h-12 text-green-400 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 class="text-lg font-semibold text-white mb-2">{{ $t('percsLanding.features.paymentAutomation.title') }}</h4>
              <p class="text-sm text-gray-500 leading-relaxed">{{ $t('percsLanding.features.paymentAutomation.description') }}</p>
            </div>

            <!-- 2. Marketplace + Integraciones -->
            <div class="features-carousel-card group">
              <div class="features-card-image bg-gradient-to-br from-pink-500/20 to-pink-600/10">
                <svg class="w-12 h-12 text-pink-400 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h4 class="text-lg font-semibold text-white mb-2">{{ $t('percsLanding.features.restAPI.title') }}</h4>
              <p class="text-sm text-gray-500 leading-relaxed">{{ $t('percsLanding.features.restAPI.description') }}</p>
            </div>

            <!-- 3. Selección IA de Inquilinos -->
            <div class="features-carousel-card group">
              <div class="features-card-image bg-gradient-to-br from-purple-500/20 to-purple-600/10">
                <ShieldCheckIcon class="w-12 h-12 text-purple-400 group-hover:scale-110 transition-transform duration-300" />
              </div>
              <h4 class="text-lg font-semibold text-white mb-2">{{ $t('percsLanding.features.aiTenantScreening.title') }}</h4>
              <p class="text-sm text-gray-500 leading-relaxed">{{ $t('percsLanding.features.aiTenantScreening.description') }}</p>
            </div>

            <!-- 4. Contratos Digitales -->
            <div class="features-carousel-card group">
              <div class="features-card-image bg-gradient-to-br from-indigo-500/20 to-indigo-600/10">
                <PencilSquareIcon class="w-12 h-12 text-indigo-400 group-hover:scale-110 transition-transform duration-300" />
              </div>
              <h4 class="text-lg font-semibold text-white mb-2">{{ $t('percsLanding.features.digitalContracts.title') }}</h4>
              <p class="text-sm text-gray-500 leading-relaxed">{{ $t('percsLanding.features.digitalContracts.description') }}</p>
            </div>

            <!-- 5. Pagos Integrados (NUEVO) -->
            <div class="features-carousel-card group">
              <div class="features-card-image bg-gradient-to-br from-emerald-500/20 to-emerald-600/10">
                <CreditCardIcon class="w-12 h-12 text-emerald-400 group-hover:scale-110 transition-transform duration-300" />
              </div>
              <h4 class="text-lg font-semibold text-white mb-2">{{ $t('percsLanding.features.payments.title') }}</h4>
              <p class="text-sm text-gray-500 leading-relaxed">{{ $t('percsLanding.features.payments.description') }}</p>
            </div>

            <!-- 6. Dashboard en Tiempo Real -->
            <div class="features-carousel-card group">
              <div class="features-card-image bg-gradient-to-br from-blue-500/20 to-blue-600/10">
                <ChartBarIcon class="w-12 h-12 text-blue-400 group-hover:scale-110 transition-transform duration-300" />
              </div>
              <h4 class="text-lg font-semibold text-white mb-2">{{ $t('percsLanding.features.analyticsDashboard.title') }}</h4>
              <p class="text-sm text-gray-500 leading-relaxed">{{ $t('percsLanding.features.analyticsDashboard.description') }}</p>
            </div>

            <!-- 7. Gestión de Clientes y Equipos -->
            <div class="features-carousel-card group">
              <div class="features-card-image bg-gradient-to-br from-orange-500/20 to-orange-600/10">
                <UsersIcon class="w-12 h-12 text-orange-400 group-hover:scale-110 transition-transform duration-300" />
              </div>
              <h4 class="text-lg font-semibold text-white mb-2">{{ $t('percsLanding.features.teamCollaboration.title') }}</h4>
              <p class="text-sm text-gray-500 leading-relaxed">{{ $t('percsLanding.features.teamCollaboration.description') }}</p>
            </div>

            <!-- 8. Estancias Flexibles (coliving-first) -->
            <div class="features-carousel-card group">
              <div class="features-card-image bg-gradient-to-br from-teal-500/20 to-teal-600/10">
                <svg class="w-12 h-12 text-teal-400 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h4 class="text-lg font-semibold text-white mb-2">{{ $t('featuresAndAudience.flexibleStays.title') }}</h4>
              <p class="text-sm text-gray-500 leading-relaxed">{{ $t('featuresAndAudience.flexibleStays.description') }}</p>
            </div>

            <!-- 9. Matching Humano (coliving-first) -->
            <div class="features-carousel-card group">
              <div class="features-card-image bg-gradient-to-br from-rose-500/20 to-rose-600/10">
                <svg class="w-12 h-12 text-rose-400 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h4 class="text-lg font-semibold text-white mb-2">{{ $t('featuresAndAudience.humanMatching.title') }}</h4>
              <p class="text-sm text-gray-500 leading-relaxed">{{ $t('featuresAndAudience.humanMatching.description') }}</p>
            </div>

            <!-- 10. Perfil Portable (coliving-first) -->
            <div class="features-carousel-card group">
              <div class="features-card-image bg-gradient-to-br from-cyan-500/20 to-cyan-600/10">
                <svg class="w-12 h-12 text-cyan-400 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                </svg>
              </div>
              <h4 class="text-lg font-semibold text-white mb-2">{{ $t('featuresAndAudience.portableProfile.title') }}</h4>
              <p class="text-sm text-gray-500 leading-relaxed">{{ $t('featuresAndAudience.portableProfile.description') }}</p>
            </div>

          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { 
  ShieldCheckIcon, 
  PencilSquareIcon, 
  UsersIcon,
  ChartBarIcon,
  CreditCardIcon
} from '@heroicons/vue/24/solid'

const { t, locale } = useI18n()

const softwareLandingPath = computed(() =>
  locale.value === 'en' ? '/property-management-software' : '/software-gestion-coliving'
)
</script>

<style scoped>
/* Carousel horizontal (estilo 11x.ai workforce carousel) */
.features-carousel {
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-snap-type: x mandatory;
}

.features-carousel::-webkit-scrollbar {
  display: none;
}

.features-carousel-track {
  display: flex;
  gap: 1.25rem;
  padding: 0 2rem;
}

@media (min-width: 640px) {
  .features-carousel-track {
    gap: 1.5rem;
    padding: 0 3rem;
  }
}

@media (min-width: 1024px) {
  .features-carousel-track {
    gap: 1.75rem;
    padding: 0 max(3rem, calc((100vw - 1100px) / 2));
  }
}

/* Cada card del carrusel */
.features-carousel-card {
  flex: 0 0 260px;
  scroll-snap-align: start;
  cursor: pointer;
}

@media (min-width: 640px) {
  .features-carousel-card {
    flex: 0 0 280px;
  }
}

@media (min-width: 1024px) {
  .features-carousel-card {
    flex: 0 0 300px;
  }
}

/* Imagen/icono de la card */
.features-card-image {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
}

.features-carousel-card:hover .features-card-image {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
}

/* Audience carousel: scroll horizontal en mobile, grid centrado en desktop */
.audience-carousel {
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-snap-type: x mandatory;
}

.audience-carousel::-webkit-scrollbar {
  display: none;
}

.audience-carousel-track {
  display: flex;
  gap: 1.25rem;
  padding: 0 2rem;
}

.audience-carousel-card {
  flex: 0 0 280px;
  scroll-snap-align: start;
}

@media (min-width: 768px) {
  .audience-carousel {
    overflow-x: visible;
  }

  .audience-carousel-track {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    max-width: 64rem;
    margin: 0 auto;
    padding: 0 2rem;
  }

  .audience-carousel-card {
    flex: none;
  }
}

@media (min-width: 1024px) {
  .audience-carousel-track {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
    max-width: 80rem;
    padding: 0 3rem;
  }
}
</style>
